import { decamelizeKeys } from 'humps';

import { apiClient } from '../apiClient';

export const PATHS = {
  SESSIONS: '/api/v2/dynamic-assessment-sessions/',
  FLOWS: '/api/v2/dynamic-assessment-sessions/flows/',
} as const;

// The onboarding survey flows served by the dynamic-assessment sessions API, in
// serve order. Mirrors Sparta's survey-region order (health conditions → goals →
// assigned screeners (AOM + state + lab preference) → diabetes decision →
// pre-diabetes → phq/gad). A future screener NOT supported by this endpoint stays
// on the legacy assigned-screener flow (see DYNAMIC_LEGACY_SCREENER_KEYS).
// Notes:
//   - the AOM refill / GLP-1 / state / lab-preference keys differ from their
//     legacy ProgramUnit.key counterparts ('aom_refill_date_screener_v1' /
//     'aom_specific_glp1_screener_v1' / 'us_state_screener_v1' /
//     'lab_preference_screener_v1');
//   - 'diabetes_decision_v1' is the dynamic counterpart of the legacy diabetes
//     TYPE screeners — webserver's `legacy_sync/diabetes.py` syncs it to
//     'diabetes_type_screener_v1', 'insulin_pump_screener_v1' and
//     'diabetes_medication_management_consent_screener_v1', and its start gate
//     requires the member to already HAVE diabetes. It is NOT the pre-diabetes
//     risk screener;
//   - 'pre_diabetes_v1' is the counterpart of the standalone pre-diabetes screen
//     (the ADA "Health Risk Survey" behind /api/v4/pre-diabetes/, scored into the
//     `prediabetes-risk` metric point), and 'phq_gad_v1' of the PHQ/GAD
//     questionnaire. Their org gating (preDiabetesRequired / phqGadRequired) is
//     evaluated SERVER-side by the flows' routing gates: an unrequired flow
//     instant-skips to a terminal.
export const DYNAMIC_ONBOARDING_FLOW_KEYS = [
  'health_conditions_v1',
  'health_goals_v1',
  'aom_screener_v1',
  'aom_refill_date_v1',
  'aom_specific_glp1_v1',
  'us_state_v1',
  'lab_preference_v1',
  'diabetes_decision_v1',
  'pre_diabetes_v1',
  'phq_gad_v1',
] as const;

export type DynamicOnboardingFlowKey = (typeof DYNAMIC_ONBOARDING_FLOW_KEYS)[number];

// ---------------------------------------------------------------------------
// Session response types
// ---------------------------------------------------------------------------

/** Node type as returned by the server. Gate/routing pages compile to 'info'. */
export type DynamicNodeType = 'question' | 'info' | 'terminal';

export type DynamicSessionStatus = 'active' | 'completed' | 'abandoned';

/**
 * Outcome fields a TERMINAL node's payload may carry. Today only the phq_gad_v1
 * flow uses them: a crisis-line result (Sparta's requiresCrisisLine +
 * qualifiedProgramUrns from the legacy PHQ/GAD POST) is surfaced on the final
 * transition response so the client can route to the crisis program screen.
 * Optional on both the session and its uiPayload — the client reads whichever
 * the server populates.
 * TODO(dynamic-assessment): confirm the exact wire shape (field names +
 * placement) against the backend before the expanded rollout.
 */
export type DynamicTerminalOutcome = {
  requiresCrisisLine?: boolean;
  qualifiedProgramUrns?: string[];
};

/**
 * The UI payload for the current node, camelized by the apiClient interceptor.
 * Present only when the session is active. `elements` is the raw SurveyJS
 * elements array — the hook/screen renders it.
 */
export type DynamicUiPayload = {
  nodeType: DynamicNodeType;
  elements: unknown[];
  title: string | null;
  description: string | null;
  sessionUuid: string;
  currentNodeExternalId: string;
  flowVersion: number;
} & DynamicTerminalOutcome;

export type DynamicSession = {
  sessionUuid: string;
  /** 'active' | 'completed' | 'abandoned' */
  status: DynamicSessionStatus;
  /**
   * Optimistic-lock counter (called `step` in the DB, `version` on the wire).
   * Pass as `expectedSessionVersion` on the next transition to guard against
   * concurrent updates.
   */
  version: number;
  currentNodeExternalId: string | null;
  flowVersion: number;
  /**
   * Null when status is 'completed' or 'abandoned' (session is past the last
   * renderable node). May be null for terminals too — check
   * `status === 'completed'` rather than inspecting ui_payload for completion.
   * When the server DOES send a terminal payload it can carry outcome fields
   * (see DynamicTerminalOutcome) — the phq_gad_v1 crisis result rides here.
   */
  uiPayload: DynamicUiPayload | null;
} & DynamicTerminalOutcome;

// ---------------------------------------------------------------------------
// Flow list types
// ---------------------------------------------------------------------------

export type DynamicFlowStatus = 'pending' | 'active' | 'completed' | 'abandoned';

export type DynamicFlowItem = {
  flowKey: string;
  status: DynamicFlowStatus;
  sessionUuid: string | null;
};

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

/**
 * Create a new session for `flowKey`, or return the existing ACTIVE session if
 * one already exists (idempotent). The returned session's current node is the
 * `start` gate (node_type: 'info') — call `transitionSession` with an empty
 * answers map to advance past it.
 */
export async function startOrResumeSession(flowKey: string): Promise<DynamicSession> {
  const res = await apiClient.post<DynamicSession>(PATHS.SESSIONS, decamelizeKeys({ flowKey }));
  return res.data;
}

/**
 * Submit answers for the current node and advance the session.
 *
 * - For `info` (gate) nodes: pass `answers = {}`.
 * - For `question` nodes: pass `answers = { [signalKey]: value }`.
 *
 * `expectedVersion` should mirror the last received `session.version` to guard
 * against concurrent updates (409 Conflict on mismatch).
 *
 * Note: `decamelizeKeys` deep-transforms the whole body, so it recurses INTO
 * `answers` and snake_cases its keys too. That's intentional and safe here — the
 * answer keys are SurveyJS element names, which are the server's `allowed_signal_key`s
 * and already snake_case, so the transform is a no-op on them. The one caveat is
 * nested object answer VALUES (e.g. a SurveyJS `matrix` value keyed by row name):
 * those nested keys would be transformed too. None of the current AOM flows use
 * such a type; if one is added, send its answer through a path that bypasses the
 * key transform.
 */
export async function transitionSession(
  sessionUuid: string,
  answers: Record<string, unknown>,
  expectedVersion?: number
): Promise<DynamicSession> {
  const res = await apiClient.post<DynamicSession>(
    `${PATHS.SESSIONS}${sessionUuid}/transition/`,
    decamelizeKeys({
      answers,
      ...(expectedVersion !== undefined && { expectedSessionVersion: expectedVersion }),
    })
  );
  return res.data;
}

/**
 * Returns the full list of published flows with their per-user status.
 * Filtered to the configured onboarding flows by the caller.
 */
export async function fetchDynamicFlows(signal?: AbortSignal): Promise<DynamicFlowItem[]> {
  const res = await apiClient.get<DynamicFlowItem[]>(PATHS.FLOWS, { signal });
  return Array.isArray(res.data) ? res.data : [];
}
